'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceToken, ZERO } from '@morpho-org/dapp-core';
import { NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import type { ForceDeallocationContext, InKindRedemptionFeeContext } from '@/components/common/VaultV2FeeRow';
import VaultV2FeeRow from '@/components/common/VaultV2FeeRow';
import type { VaultV2ActionPanelData } from '../../vaultV2ActionPanel.mapper';
import { useVaultV2SummaryData } from './useVaultV2SummaryData';
import VaultV2PositionRow from './VaultV2PositionRow';
import VaultV2SummaryApyRow from './VaultV2SummaryApyRow';
import VaultV2SummaryEarningsRow from './VaultV2SummaryEarningsRow';
interface VaultV2SummaryProps {
  vaultData: VaultV2ActionPanelData;
  vaultAsset: InterfaceToken | null | undefined;
  vaultAddress: Address;
  initialPosition: bigint | undefined;
  finalPosition: bigint | undefined;
  isLoading: boolean;
  feeAssets?: bigint;
  forceDeallocation?: ForceDeallocationContext;
  inKindRedemption?: InKindRedemptionFeeContext;
}
const VaultV2Summary = t0 => {
  const $ = _c(66);
  const {
    vaultData,
    vaultAsset,
    vaultAddress,
    initialPosition,
    finalPosition,
    isLoading,
    feeAssets,
    forceDeallocation,
    inKindRedemption
  } = t0;
  let t1;
  if ($[0] !== finalPosition || $[1] !== initialPosition || $[2] !== vaultAsset || $[3] !== vaultData) {
    t1 = {
      vaultData,
      vaultAsset,
      initialPosition,
      finalPosition
    };
    $[0] = finalPosition;
    $[1] = initialPosition;
    $[2] = vaultAsset;
    $[3] = vaultData;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const summaryData = useVaultV2SummaryData(t1);
  const chainId = vaultAsset?.chain.id;
  let t2;
  if ($[5] !== chainId) {
    t2 = chainId ? InterfaceChainUtils.getMetadata(chainId).chain.name : undefined;
    $[5] = chainId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const networkName = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-3xs text-secondary-foreground">Network</span>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      size: "small"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== chainId) {
    t5 = <Network chainId={chainId} srcAvatarProps={t4} />;
    $[9] = chainId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== networkName) {
    t6 = <span className="text-3xs">{networkName}</span>;
    $[11] = networkName;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t3}<div className="gap-2xs flex flex-row items-center">{t5}{t6}</div></div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isLoading || $[17] !== summaryData.position || $[18] !== vaultAsset) {
    t8 = <VaultV2PositionRow vaultAsset={vaultAsset} position={summaryData.position} isLoading={isLoading} />;
    $[16] = isLoading;
    $[17] = summaryData.position;
    $[18] = vaultAsset;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== summaryData.apy.formatted || $[21] !== summaryData.apy.net || $[22] !== summaryData.apy.points || $[23] !== summaryData.apy.raw || $[24] !== summaryData.apy.rewards) {
    t9 = {
      raw: summaryData.apy.raw,
      formatted: summaryData.apy.formatted,
      net: summaryData.apy.net,
      points: summaryData.apy.points,
      rewards: summaryData.apy.rewards
    };
    $[20] = summaryData.apy.formatted;
    $[21] = summaryData.apy.net;
    $[22] = summaryData.apy.points;
    $[23] = summaryData.apy.raw;
    $[24] = summaryData.apy.rewards;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== chainId || $[27] !== vaultAddress) {
    t10 = chainId ? {
      kind: "vault",
      chainId,
      identifier: vaultAddress
    } : null;
    $[26] = chainId;
    $[27] = vaultAddress;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== summaryData.apy.apyBreakdown || $[30] !== summaryData.apy.includesDonations || $[31] !== summaryData.apy.isRateCapped || $[32] !== summaryData.apy.managementFee || $[33] !== summaryData.apy.maxApy || $[34] !== summaryData.apy.maxRate || $[35] !== summaryData.apy.performanceFee || $[36] !== t10 || $[37] !== t9 || $[38] !== vaultAsset) {
    t11 = <VaultV2SummaryApyRow performanceFee={summaryData.apy.performanceFee} managementFee={summaryData.apy.managementFee} apy={t9} vaultAsset={vaultAsset} maxRate={summaryData.apy.maxRate} maxApy={summaryData.apy.maxApy} isRateCapped={summaryData.apy.isRateCapped} includesDonations={summaryData.apy.includesDonations} apyBreakdown={summaryData.apy.apyBreakdown} merklContext={t10} />;
    $[29] = summaryData.apy.apyBreakdown;
    $[30] = summaryData.apy.includesDonations;
    $[31] = summaryData.apy.isRateCapped;
    $[32] = summaryData.apy.managementFee;
    $[33] = summaryData.apy.maxApy;
    $[34] = summaryData.apy.maxRate;
    $[35] = summaryData.apy.performanceFee;
    $[36] = t10;
    $[37] = t9;
    $[38] = vaultAsset;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== isLoading || $[41] !== summaryData.projectedEarnings.monthly.final || $[42] !== summaryData.projectedEarnings.monthly.finalTooltip || $[43] !== summaryData.projectedEarnings.monthly.initial || $[44] !== summaryData.projectedEarnings.monthly.initialTooltip || $[45] !== summaryData.projectedEarnings.monthly.showFinalValue) {
    t12 = <VaultV2SummaryEarningsRow label="Projected monthly earnings" initial={summaryData.projectedEarnings.monthly.initial} initialTooltip={summaryData.projectedEarnings.monthly.initialTooltip} final={summaryData.projectedEarnings.monthly.final} finalTooltip={summaryData.projectedEarnings.monthly.finalTooltip} showFinalValue={summaryData.projectedEarnings.monthly.showFinalValue} isLoading={isLoading} />;
    $[40] = isLoading;
    $[41] = summaryData.projectedEarnings.monthly.final;
    $[42] = summaryData.projectedEarnings.monthly.finalTooltip;
    $[43] = summaryData.projectedEarnings.monthly.initial;
    $[44] = summaryData.projectedEarnings.monthly.initialTooltip;
    $[45] = summaryData.projectedEarnings.monthly.showFinalValue;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== isLoading || $[48] !== summaryData.projectedEarnings.yearly.final || $[49] !== summaryData.projectedEarnings.yearly.finalTooltip || $[50] !== summaryData.projectedEarnings.yearly.initial || $[51] !== summaryData.projectedEarnings.yearly.initialTooltip || $[52] !== summaryData.projectedEarnings.yearly.showFinalValue) {
    t13 = <VaultV2SummaryEarningsRow label="Projected yearly earnings" initial={summaryData.projectedEarnings.yearly.initial} initialTooltip={summaryData.projectedEarnings.yearly.initialTooltip} final={summaryData.projectedEarnings.yearly.final} finalTooltip={summaryData.projectedEarnings.yearly.finalTooltip} showFinalValue={summaryData.projectedEarnings.yearly.showFinalValue} isLoading={isLoading} />;
    $[47] = isLoading;
    $[48] = summaryData.projectedEarnings.yearly.final;
    $[49] = summaryData.projectedEarnings.yearly.finalTooltip;
    $[50] = summaryData.projectedEarnings.yearly.initial;
    $[51] = summaryData.projectedEarnings.yearly.initialTooltip;
    $[52] = summaryData.projectedEarnings.yearly.showFinalValue;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== feeAssets || $[55] !== forceDeallocation || $[56] !== inKindRedemption || $[57] !== vaultAsset) {
    t14 = feeAssets !== undefined && feeAssets > ZERO && <VaultV2FeeRow className="min-h-[20px]" feeAssets={feeAssets} forceDeallocation={forceDeallocation} inKindRedemption={inKindRedemption} vaultAsset={vaultAsset} />;
    $[54] = feeAssets;
    $[55] = forceDeallocation;
    $[56] = inKindRedemption;
    $[57] = vaultAsset;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== t11 || $[60] !== t12 || $[61] !== t13 || $[62] !== t14 || $[63] !== t7 || $[64] !== t8) {
    t15 = <Card className="gap-sm flex-col" withBorder={true} data-testid="vault-v2-summary">{t7}{t8}{t11}{t12}{t13}{t14}</Card>;
    $[59] = t11;
    $[60] = t12;
    $[61] = t13;
    $[62] = t14;
    $[63] = t7;
    $[64] = t8;
    $[65] = t15;
  } else {
    t15 = $[65];
  }
  return t15;
};
export default VaultV2Summary;